import { ReactComponent as OutlineDocsMinus } from "assets/svg/invoicing/outline-document-minus.svg";
import { ReactComponent as OutlineEdit2 } from "assets/svg/invoicing/outline-edit-2.svg";
import { ReactComponent as OutlineWallet } from "assets/svg/invoicing/outline-wallet.svg";
import { ReactComponent as InvoicePaid } from "assets/svg/invoicing/paid-state.svg";
import { ReactComponent as InvoiceRefunded } from "assets/svg/invoicing/refunded-state.svg";
import { ReactComponent as InvoiceWriteOff } from "assets/svg/invoicing/write-off-state.svg";
import { ReactComponent as OutlineDownload } from "assets/svg/outline-download.svg";
import { ReactComponent as OutlineMail } from "assets/svg/outline-mail.svg";
import { ReactComponent as OutlinePlus16 } from "assets/svg/outline-plus-16.svg";
import { ReactComponent as OutlinePrint } from "assets/svg/outline-print.svg";
import { ReactComponent as OutlineTrash } from "assets/svg/outline-trash-1.svg";
import { ReactComponent as OutlineTrashMini } from "assets/svg/outline-trash-mini.svg";
import { useUser } from "contexts/UserContext";
import moment from "moment";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Button, Col, Form, ListGroup, Row, Table } from "react-bootstrap";
import { Link, useNavigate, useParams } from "react-router-dom";
import InvoicePastDue from "./components/invoice-past-due";

import ConfirmModal from "components/confirm-modal";
import ErrorMessage from "components/error-message";
import Loading from "components/Loading";
import SuccessMessage from "components/success-message";
import InvoicingViewerTitle from "./components/invoicing-viewer-title";
import StatusDropdown from "./components/status-dropdown";
// styles
import styles from "./InvoicingEditorPdf.module.scss";
// utils
import IconButton from "components/icon-button";
import {
  makeImageFromUri,
  resizeImageAsBlob,
} from "components/utility/image-upload";
import deleteInvoice from "services/invoicing/deleteInvoice";
import editInvoice from "services/invoicing/editInvoice";
import emailInvoiceToPatient from "services/invoicing/emailInvoiceToPatient";
import refundInvoice from "services/invoicing/refundInvoice";
import writeOffInvoice from "services/invoicing/writeOffInvoice";
import putOrchidProCustomLogo from "services/putOrchidProCustomLogo";
import getClasses from "utils/getClasses";

import { useTimeOnPageContext } from "contexts/time-on-page";
import useFetchInvoicesForAppointments from "hooks/useFetchInvoicesForAppointments";
import { AmplitudePageName, track } from "utils/amplitude";
import { apiHostAndPath } from "utils/api";
import downloadFromDifferentOrigin from "utils/downloadFromDifferentOrigin";
import { disableStripeFeatures } from "utils/restrictAccess";
import waitForInvoice from "../../services/invoicing/wait-for-invoice";
import {
  isInvoiceClosed,
  isInvoiceFullyPaid,
  isInvoicePastDue,
  isInvoiceRefunded,
  isInvoiceWrittenOff,
} from "./utils/invoiceStatus";
import { useTeamContext } from "contexts/group-practice-context/TeamContext";
import queryAppointment from "services/queryAppointment";

const AMPLITUDE_PAGE_NAME: AmplitudePageName = "invoice-page";

const DATE_FORMAT = "M/D/YYYY";

const initEdits = {
  terms: 0,
  line_items: [
    {
      item_date: new Date().toISOString(),
      description: "",
      status: "complete",
      amount: 0,
    },
  ],
  provider_contact: "",
  patient_contact: "",
  note_entry: "",
  appointment_status: "",
};

const getActivityLogText = (activity, invoice, timezone) => {
  const {
    event_type,
    // amount_pending,
    amount_paid,
    payment,
    amount_written_off,
    amount_refunded,
  } = activity;
  if (event_type === "invoice_emailed") {
    return (
      <Row className={"mb-3"}>
        <Col className={styles["activity-log__left"]}></Col>
        <Col>
          Invoice of{" "}
          <b>{`$${(invoice.amount_total_invoiced || 0).toFixed(2)}`}</b> was
          emailed to patient.
        </Col>
      </Row>
    );
  } else if (event_type === "created") {
    return (
      <Row className={"mb-3"}>
        <Col className={styles["activity-log__left"]}>
          {moment.tz(invoice.invoice_issued_at, timezone).format(DATE_FORMAT) ||
            "-"}
        </Col>
        <Col>
          Invoice of{" "}
          <b>{`$${(invoice.amount_total_invoiced || 0).toFixed(2)}`}</b> is
          generated and marked as payment due.
        </Col>
      </Row>
    );
  } else if (event_type === "payment_success" && !!payment) {
    return (
      <Row className={"mb-3"}>
        <Col className={styles["activity-log__left"]}>
          {moment.tz(payment.payment_date, timezone).format(DATE_FORMAT)}
        </Col>
        {payment.payment_method_type === "cash" ? (
          <Col>
            Invoice of{" "}
            <b>{`$${(amount_paid || payment.total_amount || 0).toFixed(2)}`}</b>{" "}
            paid with <b>{payment.payment_method_type}</b> on{" "}
            {moment.tz(payment.payment_date, timezone).format(DATE_FORMAT)}
          </Col>
        ) : payment.payment_method_type === "check" ? (
          <Col>
            Invoice of{" "}
            <b>{`$${(amount_paid || payment.total_amount || 0).toFixed(2)}`}</b>{" "}
            paid with{" "}
            <b>
              {payment.payment_method_type}{" "}
              {payment.payment_method_text && `#${payment.payment_method_text}`}
            </b>{" "}
            on {moment.tz(payment.payment_date, timezone).format(DATE_FORMAT)}
          </Col>
        ) : payment.payment_method_type === "card" ? (
          <Col>
            Invoice of{" "}
            <b>{`$${(amount_paid || payment.total_amount || 0).toFixed(2)}`}</b>{" "}
            paid to the {payment.payment_method_text}
          </Col>
        ) : payment.payment_method_type === "other" ? (
          <Col>
            Invoice of{" "}
            <b>{`$${(amount_paid || payment.total_amount || 0).toFixed(2)}`}</b>{" "}
            paid with <b>{payment.payment_method_text}</b> on{" "}
            {moment.tz(payment.payment_date, timezone).format(DATE_FORMAT)}
          </Col>
        ) : null}
      </Row>
    );
  } else if (event_type === "refund" && !!payment) {
    return (
      <Row className={"mb-3"}>
        <Col className={styles["activity-log__left"]}>
          {moment.tz(payment.payment_date, timezone).format(DATE_FORMAT)}
        </Col>
        <Col>
          <b>{`$${(amount_refunded || payment.total_amount || 0).toFixed(
            2,
          )}`}</b>{" "}
          was refunded.
        </Col>
      </Row>
    );
  } else if (event_type === "write_off") {
    return (
      <Row className={"mb-3"}>
        <Col className={styles["activity-log__left"]}></Col>
        <Col>
          <b>{`$${(amount_written_off || 0).toFixed(2)}`}</b> was written-off
          for invoice.
        </Col>
      </Row>
    );
  }
};

const InvoicingEditorPdf = () => {
  const { setPage } = useTimeOnPageContext();
  useEffect(() => {
    setPage(AMPLITUDE_PAGE_NAME);
  }, [setPage]);

  const { user, defaultUserTimezone } = useUser();
  const navigate = useNavigate();

  const [changed, setChanged] = useState(false);
  const [edits, setEdits] = useState<InvoiceEditableData>(initEdits);
  const [error, setError] = useState<any>();
  const [success, setSuccess] = useState<string>();
  const [showWriteOffModal, setShowWriteOffModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [showRefundModal, setShowRefundModal] = useState(false);

  const params = useParams<{
    appointment_id?: string;
    type?: string;
  }>();
  const { practiceOwnerOrchidUserId } = useTeamContext();

  const [appointments, setAppointments] = useState<AppointmentMetaData[]>([]);

  useEffect(() => {
    if (!params?.appointment_id) return;
    queryAppointment(parseInt(params.appointment_id), practiceOwnerOrchidUserId)
      .then((appointment) => setAppointments([appointment]))
      .catch(console.error);
  }, [params?.appointment_id, practiceOwnerOrchidUserId]);

  const { appointmentsWithInvoices } = useFetchInvoicesForAppointments(
    appointments,
    practiceOwnerOrchidUserId,
  );

  const appointment = appointmentsWithInvoices[0];

  const [invoice, setInvoice] = useState<InvoiceData>();

  const initialized = useRef(false);

  useEffect(() => {
    if (!appointment?.appointment_id) {
      return;
    }

    if (appointment.invoice) {
      setInvoice(appointment.invoice);
    } else {
      waitForInvoice(appointment.appointment_id, practiceOwnerOrchidUserId)
        .then(setInvoice)
        .catch(console.error);
    }
  }, [
    appointment?.appointment_id,
    appointment?.invoice,
    practiceOwnerOrchidUserId,
  ]);

  useEffect(() => {
    if (!invoice || initialized.current) return;

    const nextLineItems = invoice.line_items.map((el) => ({
      ...el,
      status: invoice.appointment_status || "complete",
    }));

    setEdits({
      terms: invoice.terms,
      line_items: nextLineItems,
      provider_contact: invoice.provider_contact,
      patient_contact: invoice.patient_contact,
      note_entry: invoice.note_entry,
      appointment_status: invoice.appointment_status || "complete",
    });

    initialized.current = true;
  }, [invoice]);

  // calculate due date by invoice
  const dueDateValue = useMemo(() => {
    if (!invoice) {
      return;
    }
    return edits.terms
      ? moment
          .tz(invoice.invoice_issued_at, defaultUserTimezone)
          .add(edits.terms, "day")
          .valueOf()
      : moment.tz(invoice.invoice_issued_at, defaultUserTimezone).valueOf();
  }, [edits.terms, invoice, defaultUserTimezone]);

  const { isMinor, isCouple } = useMemo(() => {
    const isMinor = !!appointment?.guardians?.length;
    const isCouple = !!appointment?.couple?.length;
    return {
      isCouple,
      isMinor,
    };
  }, [appointment]);

  const fileUrl = useMemo(
    () => apiHostAndPath(`/api/invoicing/pdf/pdf/${invoice?.invoice_no}`),
    [invoice?.invoice_no],
  );

  console.log("current invoice data", invoice);

  const onInputChange = (event) => {
    const inputField = event.currentTarget;
    if (inputField.name === "terms") {
      updateInvoiceData(inputField.name, parseInt(inputField.value));
    } else {
      updateInvoiceData(inputField.name, inputField.value);
    }
  };

  const onLineItemInputChange = (index) => (event) => {
    const inputField = event.currentTarget;

    let nextLineItems = edits.line_items;
    nextLineItems[index] = {
      ...nextLineItems[index],
      [inputField.name]:
        inputField.name === "amount"
          ? parseFloat(inputField.value)
          : inputField.value,
    };
    updateInvoiceData("line_items", nextLineItems);
  };

  // TODO: fix date and status change for line item
  const addLineItem = () => {
    const { line_items } = edits;
    const nextLineItem = {
      item_date: new Date().toISOString(),
      description: "",
      status: edits.line_items[0]?.status,
      amount: 0,
    };
    updateInvoiceData("line_items", line_items.concat(nextLineItem));
  };

  const removeLineItem = (index) => {
    let nextLineItems = edits.line_items;
    nextLineItems.splice(index, 1);
    updateInvoiceData("line_items", nextLineItems);
  };

  const updateInvoiceData = useCallback((name, value) => {
    setEdits((edits) => ({
      ...edits,
      [name]: value,
    }));
    setChanged(true);
  }, []);

  const [imgUpdateTimestamp, setImgUpdateTimestamp] = useState(Date.now());

  let imagePreviewUrl = user
    ? apiHostAndPath(
        `/api/users/v1/orchid_pro/custom_logo?timestamp=${imgUpdateTimestamp}`,
      )
    : null;

  const imagePreview = imagePreviewUrl ? (
    <img
      src={imagePreviewUrl}
      onError={(e) => {
        (e.target as HTMLImageElement).onerror = null;
        (e.target as HTMLImageElement).src = "/img/placeholder/add-logo.png";
      }}
      alt="orchid-profile-img"
      className={styles["profile-img"]}
    />
  ) : null;

  const onImageChanged = async (imagePreviewUrl) => {
    try {
      const img = await makeImageFromUri(imagePreviewUrl);
      const blob = await resizeImageAsBlob(img, 800, 800);
      await putOrchidProCustomLogo(await blob);
      setImgUpdateTimestamp(Date.now());
    } catch (err) {
      console.error("failed to set profile image", err);
    }
  };

  const handleImageChange = (e) => {
    e.preventDefault();
    let reader = new FileReader();
    let file = e.target.files[0];
    reader.onloadend = (e) => {
      onImageChanged(e.target?.result);
    };
    if (file) reader.readAsDataURL(file);
  };

  const onSave = async () => {
    if (!invoice) return;
    try {
      await editInvoice(invoice.invoice_no, edits, practiceOwnerOrchidUserId);
      setChanged(true);
      navigate(
        `/pro-dashboard/invoicing/view/appointment/${params.appointment_id}`,
        { replace: true }
      );
    } catch (error) {
      setError(error);
    }
  };

  // this can be removed once websocket is implemented
  const [invoiceState, setInvoiceState] = useState<string | null>(null);

  const onDelete = async () => {
    if (!invoice) return;
    try {
      await deleteInvoice(invoice.invoice_no);
      setShowDeleteModal(false);
      //  if delete is success, go back to calendar
      navigate("/pro-dashboard/schedule");
    } catch (error) {
      setError(error);
    }
  };

  const onWriteOff = async () => {
    if (!invoice) return;
    try {
      const response = await writeOffInvoice(invoice.invoice_no);
      if (isInvoiceWrittenOff(response)) {
        // if in view mode
        setInvoiceState("write_off");

        // if in edit mode mode, redirect to view mode
        if (params?.type === "edit" && params?.appointment_id) {
          navigate(
            `/pro-dashboard/invoicing/view/appointment/${params.appointment_id}`,
          );
        }
      }
      setShowWriteOffModal(false);
    } catch (error) {
      setError(error);
    }
  };

  const onRefund = async () => {
    if (!invoice) return;
    try {
      await refundInvoice(invoice.invoice_no);
      setShowRefundModal(false);
    } catch (error) {
      setError(error);
    } finally {
      window.location.reload();
    }
  };

  const onEmailPatient = async () => {
    if (!invoice) return;
    try {
      await emailInvoiceToPatient(invoice.invoice_no);
      setSuccess(
        `Invoice is sent to ${invoice.bill_to_contact.first_name} ${invoice.bill_to_contact.last_name} at ${invoice.bill_to_contact.email}`,
      );
    } catch (error) {
      setError(error);
    }
  };

  const onDownload = useCallback(() => {
    downloadFromDifferentOrigin(fileUrl, `${invoice?.invoice_no}.pdf`).catch(
      console.error,
    );
  }, [fileUrl, invoice?.invoice_no]);

  return !invoice || !params?.type ? (
    <div className={styles["please-wait"]}>
      <Loading />
      <h4 className={"deep-blue rg-24"}>Please wait.</h4>
      <p className={"rg-18 gray01"}>
        Invoice is being generated. It will take about 1-2 minutes. Please
        refresh if you can't view the page.
      </p>
    </div>
  ) : (
    <div className={styles["invoicing-editor"]}>
      {error && (
        <ErrorMessage
          show={true}
          onHide={() => setError(undefined)}
          errorMsg={error.toString()}
          className={styles["invoicing-editor__error-message"]}
        />
      )}

      {success && (
        <SuccessMessage
          show={true}
          onHide={() => setSuccess("")}
          message={success}
          duration={1000}
        />
      )}

      {/* confirm delete modal */}
      <ConfirmModal
        labelTitle={"Delete Invoice?"}
        children={"Are you sure you want to delete the invoice?"}
        labelCancel={"Cancel"}
        labelConfirm={"Delete invoice"}
        show={showDeleteModal}
        onCancel={() => setShowDeleteModal(false)}
        onConfirm={() => onDelete()}
      />

      {/* confirm refund modal */}
      <ConfirmModal
        labelTitle={"Refund?"}
        children={"Do you want to refund this invoice?"}
        labelCancel={"Cancel"}
        labelConfirm={"Issue Refund"}
        show={showRefundModal}
        onCancel={() => setShowRefundModal(false)}
        onConfirm={() => onRefund()}
      />

      {/* confirm writeoff modal */}
      <ConfirmModal
        labelTitle={"Write-off?"}
        children={"Do you want to write off this invoice?"}
        labelCancel={"Cancel"}
        labelConfirm={"Write-off"}
        show={showWriteOffModal}
        onCancel={() => setShowWriteOffModal(false)}
        onConfirm={() => onWriteOff()}
      />

      <div className={styles["invoicing-editor__actions"]}>
        <InvoicingViewerTitle
          onClose={() => {
            track(AMPLITUDE_PAGE_NAME, "close-window-button", "click");
            window.close();
          }}
          contact={invoice.bill_to_contact}
        />
        <div className={styles["invoicing-editor__right-actions"]}>
          {params.type === "edit" ? (
            <Button
              onClick={() => {
                track(AMPLITUDE_PAGE_NAME, "save-button", "click");
                onSave();
              }}
            >
              Save
            </Button>
          ) : (
            <>
              {changed && <div className={"rg-14 mr-4"}>Saved</div>}
              {!isInvoiceClosed(invoice) && invoiceState !== "write_off" && (
                <Button
                  as={Link as any}
                  to={`/pro-dashboard/add-client-payment/${
                    (isMinor || isCouple) && appointment
                      ? appointment.patient.orchid_user_id
                      : invoice.bill_to_user_id
                  }/${invoice.invoice_no}`}
                  className={styles["add-payment-button"]}
                  onClick={() =>
                    track(AMPLITUDE_PAGE_NAME, "add-payment-button", "click")
                  }
                >
                  Add Payment
                </Button>
              )}
            </>
          )}
        </div>
      </div>
      <div className={styles["invoicing-editor-area"]}>
        <div
          className={getClasses(
            params.type === "view" && styles["invoicing-editor-pdf--view"],
            styles["invoicing-editor-pdf"],
          )}
        >
          <div className={styles["invoicing-editor-pdf__state-tag"]}>
            {isInvoiceFullyPaid(invoice) ? (
              <InvoicePaid />
            ) : isInvoiceWrittenOff(invoice) || invoiceState === "write_off" ? (
              <InvoiceWriteOff />
            ) : isInvoiceRefunded(invoice) ? (
              <InvoiceRefunded />
            ) : isInvoicePastDue(invoice) ? (
              !dueDateValue ? null : (
                <InvoicePastDue
                  days={moment()
                    .tz(defaultUserTimezone)
                    .diff(moment.tz(dueDateValue, defaultUserTimezone), "day")}
                />
              )
            ) : null}
          </div>
          <div className={styles["invoicing-editor-pdf__content"]}>
            <Row>
              <Col className={styles["invoicing-editor-pdf__section-1-left"]}>
                <div className={"gray01 rg-8"}>FROM</div>
                <div className={"rg-9"}>
                  {`${invoice.provider_business_contact.first_name} ${invoice.provider_business_contact.last_name}`}
                </div>
                <h2 className={styles["invoicing-editor-pdf__section-1-title"]}>
                  Invoice
                </h2>
              </Col>
              <Col className={styles["invoicing-editor-pdf__section-1-right"]}>
                <Form.Group className={styles["image-preview"]}>
                  <Form.Control
                    type="file"
                    onChange={(e) => handleImageChange(e)}
                    id="customLogo"
                    disabled={params.type === "view"}
                    accept="image/*"
                    style={{ display: 'none' }}
                  />
                  <Form.Label
                    htmlFor="customLogo"
                    className={getClasses(
                      styles["invoicing-editor-pdf__section-1-logo-area"],
                      "d-block"
                    )}
                    style={{ cursor: params.type === "view" ? "default" : "pointer" }}
                  >
                    {imagePreview ? (
                      imagePreview
                    ) : (
                      "Add your logo from profile"
                    )}
                  </Form.Label>
                </Form.Group>
              </Col>
            </Row>
            <Row className={styles["invoicing-editor-pdf__section-2"]}>
              <Col
                className={getClasses(
                  "pl-0",
                  styles["invoicing-editor-pdf__section-2-biz-name"],
                )}
              >
                <b>{`${invoice.provider_business_contact.business_entity_name}`}</b>
                <br />
                <div>{`${
                  invoice.provider_business_contact.office_address_line1
                } ${
                  invoice.provider_business_contact.office_address_line2
                    ? invoice.provider_business_contact.office_address_line2
                    : ""
                }`}</div>
                <div>
                  {invoice.provider_business_contact.office_city
                    ? `${invoice.provider_business_contact.office_city},`
                    : ""}{" "}
                  {invoice.provider_business_contact.office_state
                    ? `${invoice.provider_business_contact.office_state},`
                    : ""}{" "}
                  {invoice.provider_business_contact.office_zipcode}
                </div>
                {invoice.provider_business_contact.phone_number ? (
                  <div>
                    <b className={"mr-1"}>Phone:</b>
                    <span>
                      {invoice.provider_business_contact.phone_number}
                    </span>
                  </div>
                ) : null}
                <div>
                  <b className={"mr-1"}>Email:</b>
                  <span>{`${invoice.provider_business_contact.email}`}</span>
                </div>
              </Col>
              <Col
                className={styles["invoicing-editor-pdf__section-2-bill-to"]}
              >
                <div className={"gray01"}>{"BILL TO"}</div>
                <b>{`${invoice.bill_to_contact.first_name} ${invoice.bill_to_contact.last_name}`}</b>
                <div>
                  {invoice.bill_to_contact.address_line1}{" "}
                  {invoice.bill_to_contact.address_line2}
                </div>
                <div>
                  {invoice.bill_to_contact.city
                    ? `${invoice.bill_to_contact.city},`
                    : ""}{" "}
                  {invoice.bill_to_contact.state
                    ? `${invoice.bill_to_contact.state},`
                    : ""}{" "}
                  {invoice.bill_to_contact.zipcode}
                </div>
              </Col>
              <Col
                className={styles["invoicing-editor-pdf__section-2-details"]}
              >
                <div className={"gray01"}>{`DETAILS`}</div>
                <div>
                  <b className={"mr-1"}>Invoice #:</b>
                  <span>{invoice.invoice_no}</span>
                  <div>
                    <b className={"mr-1"}>Issued:</b>
                    <span>
                      {moment
                        .tz(invoice.invoice_issued_at, defaultUserTimezone)
                        .format(DATE_FORMAT)}
                    </span>
                  </div>
                  <div className={"d-flex align-items-center"}>
                    <b className={"mr-1"}>Terms:</b>
                    <span>
                      {params.type === "edit" ? (
                        <Form.Group className={"mb-0"}>
                          <Form.Control
                            className={
                              styles[
                                "invoicing-editor-pdf__section-2-form-control"
                              ]
                            }
                            type="text"
                            placeholder={`Net ${edits.terms || 0}`}
                            name="terms"
                            value={edits.terms || ""}
                            onChange={onInputChange}
                            onFocus={() =>
                              track(AMPLITUDE_PAGE_NAME, "terms-field", "focus")
                            }
                          />
                        </Form.Group>
                      ) : (
                        `Net ${edits.terms || 0}`
                      )}
                    </span>
                  </div>
                  <div>
                    <b className={"mr-1"}>Due:</b>
                    {!dueDateValue
                      ? ""
                      : moment
                          .tz(dueDateValue, defaultUserTimezone)
                          .format(DATE_FORMAT)}
                  </div>
                </div>
              </Col>
            </Row>
            <Row className={styles["invoicing-editor-pdf__section-3"]}>
              <Col
                className={getClasses(
                  "pr-1",
                  styles["invoicing-editor-pdf__section-3-left"],
                )}
              >
                <Form.Group className={"mb-0"} controlId="patientContact">
                  <Form.Label
                    className={styles["invoicing-editor-pdf__form-label"]}
                  >
                    PATIENT
                  </Form.Label>
                  {params.type === "edit" ? (
                    <Form.Control
                      as="textarea"
                      rows={3}
                      name="patient_contact"
                      value={edits.patient_contact || ""}
                      // maxLength={2000}
                      onChange={onInputChange}
                    />
                  ) : (
                    <div
                      className={
                        styles["invoicing-editor-pdf__section-3-content"]
                      }
                    >
                      {edits.patient_contact}
                    </div>
                  )}
                </Form.Group>
              </Col>
              <Col
                className={getClasses(
                  "pl-1",
                  styles["invoicing-editor-pdf__section-3-right"],
                )}
              >
                <Form.Group className={"mb-0"} controlId="providerContact">
                  <Form.Label
                    className={styles["invoicing-editor-pdf__form-label"]}
                  >
                    PROVIDER
                  </Form.Label>

                  {params.type === "edit" ? (
                    <Form.Control
                      as="textarea"
                      rows={3}
                      name="provider_contact"
                      value={edits.provider_contact || ""}
                      // maxLength={2000}
                      onChange={onInputChange}
                      onFocus={() =>
                        track(AMPLITUDE_PAGE_NAME, "provider-field", "focus")
                      }
                    />
                  ) : (
                    <div
                      className={
                        styles["invoicing-editor-pdf__section-3-content"]
                      }
                    >
                      {edits.provider_contact}
                    </div>
                  )}
                </Form.Group>
              </Col>
            </Row>
            <hr className={styles["invoicing-editor-pdf__hr"]} />
            <div className={styles["invoicing-editor-pdf__item-area"]}>
              <Table
                className={getClasses(
                  "table-flush",
                  styles["invoicing-editor-pdf__table"],
                )}
              >
                <thead>
                  <tr className="bd-12">
                    <th>DATE</th>
                    <th>DESCRIPTION</th>
                    <th>STATUS</th>
                    <th className={"text-right"}>AMOUNT</th>
                    <th></th>
                  </tr>
                </thead>
                <tbody>
                  {edits.line_items.map((lineItem, index) => {
                    return (
                      <tr key={index}>
                        <td
                          className={styles["invoicing-editor-pdf__table-td-1"]}
                        >
                          {moment
                            .tz(
                              appointment?.appointment_start,
                              defaultUserTimezone,
                            )
                            .format(DATE_FORMAT)}
                        </td>
                        <td className="gray01">
                          {params.type === "edit" ? (
                            <Form.Group className={"mb-0"}>
                              <Form.Control
                                className={
                                  styles[
                                    "invoicing-editor-pdf__item-area-form-control"
                                  ]
                                }
                                type="text"
                                placeholder="description"
                                name="description"
                                value={lineItem.description || ""}
                                onChange={onLineItemInputChange(index)}
                                onFocus={() =>
                                  track(
                                    AMPLITUDE_PAGE_NAME,
                                    "patient-field",
                                    "focus",
                                  )
                                }
                              />
                            </Form.Group>
                          ) : (
                            lineItem.description
                          )}
                        </td>
                        <td className={""}>
                          {index === 0 && (
                            <StatusDropdown
                              type={params.type}
                              itemStatus={edits.appointment_status}
                              onItemStatusChange={(status) =>
                                updateInvoiceData("appointment_status", status)
                              }
                            />
                          )}
                        </td>
                        <td className={"text-right"}>
                          {params.type === "edit" ? (
                            <Form.Group className={"mb-0"}>
                              <Form.Control
                                className={getClasses(
                                  "text-right",
                                  styles[
                                    "invoicing-editor-pdf__item-area-form-control"
                                  ],
                                )}
                                type="number"
                                step="0.01"
                                min="0"
                                placeholder="amount"
                                name="amount"
                                value={lineItem.amount || ""}
                                onChange={onLineItemInputChange(index)}
                                onFocus={() =>
                                  track(
                                    AMPLITUDE_PAGE_NAME,
                                    "amount-field",
                                    "focus",
                                  )
                                }
                              />
                            </Form.Group>
                          ) : (
                            lineItem.amount
                          )}
                        </td>
                        <td className={"px-2 border-0 position-absolute"}>
                          {index >= 1 && params.type === "edit" && (
                            <IconButton
                              onClick={() => {
                                track(
                                  AMPLITUDE_PAGE_NAME,
                                  "remove-line-item-button",
                                  "click",
                                );
                                removeLineItem(index);
                              }}
                            >
                              <OutlineTrashMini />
                            </IconButton>
                          )}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </Table>
              {params.type === "edit" && (
                <Button
                  variant="link"
                  className="d-flex align-items-center mb-3 p-0"
                  onClick={() => {
                    track(AMPLITUDE_PAGE_NAME, "add-line-item-button", "click");
                    addLineItem();
                  }}
                >
                  <OutlinePlus16 className="mr-1" />
                  Add Line Item
                </Button>
              )}
            </div>

            <div className={styles["invoicing-editor-pdf__section-5"]}>
              <div
                className={
                  styles["invoicing-editor-pdf__section-5-summary-block"]
                }
              >
                <Row>
                  <Col className={"text-right"}>Subtotal</Col>
                  <Col className={"text-right"}>
                    {`$${(edits.line_items.length > 0
                      ? edits.line_items
                          .map(({ amount }) => amount || 0)
                          .reduce((a, b) => a + b, 0)
                      : 0
                    ).toFixed(2)}`}
                  </Col>
                </Row>
                <Row
                  className={
                    styles["invoicing-editor-pdf__section-5-total-row"]
                  }
                >
                  <Col className={"text-right"}>
                    <div>Total</div>
                    <div>Amount Paid</div>
                    {isInvoiceRefunded(invoice) && <div>Refund</div>}
                  </Col>
                  <Col className={"text-right"}>
                    <div>
                      {`$${(edits.line_items.length > 0
                        ? edits.line_items
                            .map(({ amount }) => amount || 0)
                            .reduce((a, b) => a + b, 0)
                        : 0
                      ).toFixed(2)}`}
                    </div>
                    <div>{`$${(invoice.amount_total_paid || 0).toFixed(
                      2,
                    )}`}</div>
                    {
                      <div>
                        {isInvoiceRefunded(invoice) &&
                          `$${(invoice.amount_total_refunded || 0).toFixed(2)}`}
                      </div>
                    }
                  </Col>
                </Row>
                <Row className={"mt-2"}>
                  <Col className={"bd-16 text-right"}>Open Balance</Col>
                  <Col className={"bd-16 deep-orange text-right"}>
                    {`$${(edits.line_items.length > 0
                      ? edits.line_items
                          .map(({ amount }) => amount || 0)
                          .reduce((a, b) => a + b, 0) -
                        invoice.amount_total_paid +
                        invoice.amount_total_refunded
                      : invoice.amount_total_invoiced -
                        invoice.amount_total_paid +
                        invoice.amount_total_refunded
                    ).toFixed(2)}`}
                  </Col>
                </Row>
              </div>
            </div>
            <div className={styles["invoicing-editor-pdf__footer"]}>
              <div className={styles["invoicing-editor-pdf__footer-content"]}>
                {params.type === "edit" ? (
                  <Form.Group className={styles["invoicing-editor-pdf__note"]}>
                    <Form.Label
                      className={styles["invoicing-editor-pdf__form-label"]}
                    >
                      NOTE
                    </Form.Label>
                    <Form.Control
                      as="textarea"
                      rows={3}
                      name="note_entry"
                      placeholder="Enter Note here"
                      value={edits.note_entry || ""}
                      // maxLength={2000}
                      onChange={onInputChange}
                      onFocus={() =>
                        track(AMPLITUDE_PAGE_NAME, "note-field", "focus")
                      }
                    />
                  </Form.Group>
                ) : (
                  edits.note_entry && (
                    <>
                      <div
                        className={styles["invoicing-editor-pdf__form-label"]}
                      >
                        NOTE
                      </div>
                      <div
                        className={
                          styles["invoicing-editor-pdf__footer-note-content"]
                        }
                      >
                        {edits.note_entry}
                      </div>
                    </>
                  )
                )}

                <hr className={styles["invoicing-editor-pdf__footer-hr"]} />
                <Row className={styles["invoicing-editor-pdf__footer-row"]}>
                  <div className={"d-flex gray01"}>
                    Powered by <b className={"ml-1"}>Orchid Exchange Inc</b>
                  </div>
                  <div className={"gray01"}>
                    1000 N. West St, Ste 1200 Wilmington, DE 19801
                  </div>
                </Row>
              </div>
            </div>
          </div>
          {params.type === "edit" ? (
            // invoice edit mode
            <ListGroup className={styles["invoicing-editor__floating-note"]}>
              <ListGroup.Item
                onClick={() => {
                  track(AMPLITUDE_PAGE_NAME, "delete-invoice-button", "click");
                  setShowDeleteModal(true);
                }}
                className={getClasses(
                  "red",
                  styles["invoicing-viewer__list-item"],
                )}
              >
                <OutlineTrash /> Delete Invoice
              </ListGroup.Item>
            </ListGroup>
          ) : (
            // invoice view mode
            <ListGroup className={styles["invoicing-editor__floating-note"]}>
              {!isInvoiceClosed(invoice) && invoiceState !== "write_off" && (
                <ListGroup.Item
                  onClick={() => {
                    track(AMPLITUDE_PAGE_NAME, "edit-invoice-button", "click");
                    navigate(
                      `/pro-dashboard/invoicing/edit/appointment/${params.appointment_id}`,
                      { replace: true }
                    );
                  }}
                  className={styles["invoicing-viewer__list-item"]}
                >
                  <OutlineEdit2 /> Edit Invoice
                </ListGroup.Item>
              )}
              {invoiceState !== "write_off" &&
                !isInvoiceWrittenOff(invoice) &&
                !isInvoiceFullyPaid(invoice) &&
                !disableStripeFeatures(user) && (
                  <ListGroup.Item
                    onClick={() => {
                      track(
                        AMPLITUDE_PAGE_NAME,
                        "email-to-patient-button",
                        "click",
                      );
                      onEmailPatient();
                    }}
                    className={styles["invoicing-viewer__list-item"]}
                  >
                    <OutlineMail /> Email to Patient
                  </ListGroup.Item>
                )}
              <ListGroup.Item
                onClick={() => {
                  track(AMPLITUDE_PAGE_NAME, "print-pdf-button", "click");
                  // open as popup
                  window.open(fileUrl, "print", "popup");
                  return false;
                }}
                className={styles["invoicing-viewer__list-item"]}
              >
                <OutlinePrint /> Print PDF
              </ListGroup.Item>
              <ListGroup.Item
                onClick={() => {
                  track(
                    AMPLITUDE_PAGE_NAME,
                    "download-invoice-button",
                    "click",
                  );
                  onDownload();
                }}
                className={styles["invoicing-viewer__list-item"]}
              >
                <OutlineDownload /> Download Invoice
              </ListGroup.Item>
              {!isInvoiceRefunded(invoice) && !isInvoiceWrittenOff(invoice) && (
                <hr className={"m-0"} />
              )}
              {!isInvoiceFullyPaid(invoice) &&
                !isInvoiceRefunded(invoice) &&
                !isInvoiceWrittenOff(invoice) &&
                invoiceState !== "write_off" && (
                  <ListGroup.Item
                    onClick={() => {
                      track(AMPLITUDE_PAGE_NAME, "write-off-button", "click");
                      setShowWriteOffModal(true);
                    }}
                    className={styles["invoicing-viewer__list-item"]}
                  >
                    <OutlineDocsMinus /> Write-off
                  </ListGroup.Item>
                )}
              {!isInvoiceFullyPaid(invoice) && (
                <ListGroup.Item
                  onClick={() => {
                    track(
                      AMPLITUDE_PAGE_NAME,
                      "delete-invoice-button",
                      "click",
                    );

                    setShowDeleteModal(true);
                  }}
                  className={getClasses(
                    "red",
                    styles["invoicing-viewer__list-item"],
                  )}
                >
                  <OutlineTrash /> Delete Invoice
                </ListGroup.Item>
              )}
              {isInvoiceFullyPaid(invoice) && (
                <ListGroup.Item
                  className={styles["invoicing-viewer__list-item"]}
                  onClick={() => {
                    track(
                      AMPLITUDE_PAGE_NAME,
                      "issue-a-refund-button",
                      "click",
                    );
                    setShowRefundModal(true);
                  }}
                >
                  <OutlineWallet />
                  Issue a refund
                </ListGroup.Item>
              )}
            </ListGroup>
          )}
        </div>
        {params.type === "view" && invoice && (
          <div className={styles["activity-log"]}>
            <div className={"bd-16 mb-4"}>Activity Log</div>
            {invoice.activity_log.map((activity, i) => {
              return (
                <div key={i}>
                  {getActivityLogText(activity, invoice, defaultUserTimezone)}
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
};

export default InvoicingEditorPdf;
